// TestSession.jsx
import { useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import TestScreen from "./TestScreen";
import StudentCheckIn from "./Utility/StudentCheckIn";
import QuestionBriefing from "./Utility/QuestionBriefing";
import useTestSession from "./Workers/useTestSession";
import useProctoring from "./Workers/Useproctoring";
import { useAntiCheat } from "./Workers/useAntiCheat";

export default function TestSession() {
    const { testId } = useParams();

    const {
        loading,
        error,
        phase,
        currentQuestion,
        questionIndex,
        totalQuestions,
        handleCheckInComplete,
        handleBriefingStart,
        handleSubmit
    } = useTestSession(testId);

    const proctoring = useProctoring();
    const submitRef = useRef(null);
    const antiCheat = useAntiCheat(phase === "question", () => submitRef.current?.());

    // ── Start/stop recording based on phase ──
    useEffect(() => {
        if (phase === "question") {
            proctoring.startRecording();
        }
        if (phase === "finished") {
            proctoring.stopRecording();
            proctoring.cleanup();
        }
        // Stop recording when leaving question phase (e.g., going to briefing)
        if (phase === "briefing") {
            proctoring.stopRecording();
        }
    }, [phase]);


    const handleSubmitWithCleanup = (answer) => {
        const violations = antiCheat.getViolations();
        handleSubmit(answer, violations);
        antiCheat.exitFullscreen();
        antiCheat.clearViolations();
    };

    submitRef.current = handleSubmitWithCleanup;  // always up to date


    // ── Loading ──
    if (loading) {
        return (
            <div style={s.center}>
                <div style={s.loadingText}>Loading...</div>
            </div>
        );
    }

    // ── Error ──
    if (error) {
        return (
            <div style={s.center}>
                <div style={s.errorText}>{error}</div>
                <button onClick={() => window.location.reload()} style={s.retryBtn}>
                    Retry
                </button>
            </div>
        );
    }

    // ── Finished ──
    if (phase === "finished") {
        return (
            <div style={s.center}>
                <div style={{ fontSize: 48 }}>🎉</div>
                <div style={s.finishedTitle}>Test Complete!</div>
                <div style={s.finishedSub}>
                    You answered {totalQuestions} question{totalQuestions !== 1 ? "s" : ""}.
                </div>
            </div>
        );
    }

    // ── Check-in ──
    // StudentCheckIn now includes its own full-screen wrapper (merged).
    // It receives the entire proctoring object and renders cam/mic status.
    if (phase === "checkin") {
        return (
            <StudentCheckIn
                proctoring={proctoring}
                onStart={(studentFields) => {
                    // studentFields = { name, className, roll }
                    // Calibration data lives inside proctoring — no need to pass it
                    handleCheckInComplete(studentFields);
                }}
            />
        );
    }

    // ── Briefing ──
    if (phase === "briefing") {
        return (
            <>
                {/* Hidden video — keeps stream attached so proctoring stays live */}
                <video
                    ref={proctoring.videoRef}
                    style={s.hiddenVideo}
                    autoPlay
                    playsInline
                    muted
                />
                <BriefingWrapper
                    questionNumber={questionIndex + 1}
                    totalQuestions={totalQuestions}
                    timeLimit={currentQuestion?.time ?? 90}
                    onStart={() => {
                        antiCheat.enterFullscreen(); // ← user gesture here
                        handleBriefingStart();
                    }}
                />
            </>
        );
    }

    // ── Question ──

    // Add this just before your phase === "question" return:
    if (phase === "question" && !antiCheat.isFullscreen) {
        const violations = antiCheat.getViolations();
        return (
            <div style={{
                position: "fixed", inset: 0, background: "#000",
                display: "flex", flexDirection: "column",
                alignItems: "center", justifyContent: "center", zIndex: 9999,
                fontFamily: "monospace",
            }}>
                <div style={{ color: "#fff", fontSize: 20, marginBottom: 24 }}>
                    Please return to fullscreen to continue your test.
                </div>
                <button
                    onClick={antiCheat.enterFullscreen}
                    style={{
                        padding: "12px 32px", fontSize: 16,
                        background: "#4f46e5", color: "#fff",
                        border: "none", borderRadius: 8, cursor: "pointer",
                        marginBottom: 32,
                    }}
                >
                    Re-enter Fullscreen
                </button>

                {/* ── DEV PANEL ── remove before production */}
                <div style={{
                    background: "#111", border: "1px solid #333",
                    borderRadius: 8, padding: 20, width: "min(90vw, 560px)",
                    color: "#0f0", fontSize: 13,
                }}>
                    <div style={{ color: "#ff0", marginBottom: 12, fontSize: 14 }}>
                        🛠 DEV — Anti-Cheat State
                    </div>

                    <div style={{ marginBottom: 8 }}>
                        <span style={{ color: "#888" }}>isViolating: </span>
                        <span style={{ color: antiCheat.isViolating ? "#f55" : "#0f0" }}>
                            {String(antiCheat.isViolating)}
                        </span>
                    </div>

                    <div style={{ marginBottom: 8 }}>
                        <span style={{ color: "#888" }}>isFullscreen: </span>
                        <span style={{ color: antiCheat.isFullscreen ? "#0f0" : "#f55" }}>
                            {String(antiCheat.isFullscreen)}
                        </span>
                    </div>

                    <div style={{ marginBottom: 16 }}>
                        <span style={{ color: "#888" }}>totalTimeOut: </span>
                        <span>{antiCheat.getTotalTimeOutSeconds()}s</span>
                    </div>

                    <div style={{ color: "#888", marginBottom: 6 }}>
                        violations ({violations.length}):
                    </div>
                    <div style={{ maxHeight: 200, overflowY: "auto" }}>
                        {violations.length === 0 && (
                            <div style={{ color: "#555" }}>none yet</div>
                        )}
                        {violations.map((v, i) => (
                            <div key={i} style={{
                                background: "#1a1a1a", borderRadius: 4,
                                padding: "6px 10px", marginBottom: 4,
                            }}>
                                <span style={{ color: "#f90" }}>{v.reason}</span>
                                {"  "}
                                <span style={{ color: "#888" }}>start:</span> {new Date(v.startTime).toLocaleTimeString()}
                                {"  "}
                                <span style={{ color: "#888" }}>end:</span>{" "}
                                {v.endTime
                                    ? new Date(v.endTime).toLocaleTimeString()
                                    : <span style={{ color: "#f55" }}>open</span>
                                }
                                {"  "}
                                <span style={{ color: "#888" }}>dur:</span>{" "}
                                {v.endTime
                                    ? `${((v.endTime - v.startTime) / 1000).toFixed(1)}s`
                                    : "—"
                                }
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }
    if (phase === "question") {
        return (
            <>
                {/* Hidden video — keeps stream attached for recording + future analysis */}
                <video
                    ref={proctoring.videoRef}
                    style={s.hiddenVideo}
                    autoPlay
                    playsInline
                    muted
                />
                <TestScreen
                    question={currentQuestion}
                    onhandleSubmit={handleSubmitWithCleanup}
                />
            </>
        );
    }

    return null;
}

// ── BriefingWrapper (keep as-is from your original code) ──


function BriefingWrapper({ questionNumber, totalQuestions, timeLimit, onStart }) {
    return (
        <div style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "100vw",
            height: "100vh",
            background: "#f8f7ff",
        }}>
            <div style={{
                width: "min(94vw, 960px)",
                maxHeight: "94vh",
                overflow: "auto",
                borderRadius: 24,
                background: "#ffffff",
                boxShadow: "0 0 0 1px rgba(0,0,0,0.03), 0 12px 48px rgba(0,0,0,0.08)",
                padding: 16,
            }}>
                <QuestionBriefing
                    questionNumber={questionNumber}
                    totalQuestions={totalQuestions}
                    timeLimit={timeLimit}
                    onStart={onStart}
                />
            </div>
        </div>
    );
}

// ── Styles ──

const s = {
    center: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "100vh",
        fontFamily: "'Nunito', sans-serif",
        gap: 16,
    },
    loadingText: {
        fontSize: 18,
        color: "#666",
    },
    errorText: {
        fontSize: 18,
        color: "#EF4444",
    },
    retryBtn: {
        padding: "10px 24px",
        borderRadius: 8,
        border: "1.5px solid #E5E7EB",
        background: "#fff",
        cursor: "pointer",
        fontSize: 14,
        fontWeight: 600,
    },
    finishedTitle: {
        fontSize: 24,
        fontWeight: 800,
        color: "#1F2937",
    },
    finishedSub: {
        fontSize: 16,
        color: "#6B7280",
    },
    hiddenVideo: {
        position: "absolute",
        width: 0,
        height: 0,
        opacity: 0,
        pointerEvents: "none",
    },
};


